/* assets/shipping-protection.js
   "Add Shipping Protection" toggle for the cart drawer.

   Mirrors assets/gift-wrapping.js and uses the confirmed <mini-cart> API
   (getSectionsToRender(), renderContents(), open()) — the same pattern as
   assets/customdt-card.js — so it survives the drawer's fetch→innerHTML
   re-renders (it re-upgrades and runs connectedCallback each time) and
   updates the cart in place without a full page reload.

   Enabled (checked) by default = opt-out: the protection is auto-added when the
   shopper views the cart drawer, and a sessionStorage flag remembers an explicit
   removal for the visit so the auto-add doesn't immediately undo an uncheck. */
(function () {
  var VARIANT_ID = 43940094869569;
  var OPT_OUT_KEY = 'dt_shipprot_optout';

  // Shared across every <shipping-protection> instance (not per-instance state) —
  // on a fresh session, <mini-cart>'s own one-time IntersectionObserver-triggered
  // refresh (assets/cart-drawer.js handleIntersection, which independently
  // fetches and overwrites #mini-cart's innerHTML the first time the drawer
  // becomes visible) can land close to a normal add-to-cart's own render, each
  // mounting its own fresh <shipping-protection> instance. Both instances'
  // this.inCart reads "false" from the DOM at mount time, so without a guard
  // that survives across instances, both independently call add() before either
  // request completes, and the cart ends up with quantity 2. This flag closes
  // that race regardless of which two renders happen to overlap.
  var addInFlight = false;

  function optedOut() {
    try { return sessionStorage.getItem(OPT_OUT_KEY) === '1'; } catch (e) { return false; }
  }
  function setOptedOut(on) {
    try {
      if (on) sessionStorage.setItem(OPT_OUT_KEY, '1');
      else sessionStorage.removeItem(OPT_OUT_KEY);
    } catch (e) { /* ignore (private mode / disabled storage) */ }
  }

  class ShippingProtection extends HTMLElement {
    connectedCallback() {
      this.miniCart = document.querySelector('mini-cart');
      this.check = this.querySelector('[data-shipprot-check]');
      this.inCart = this.dataset.inCart === 'true';
      this.otherItems = parseInt(this.dataset.otherItems || '0', 10) || 0;
      if (!this.check) return;

      // Toggle: check → add (+ clear opt-out); uncheck → remove (+ remember opt-out).
      this.check.addEventListener('change', () => {
        if (this.check.checked) {
          setOptedOut(false);
          this.add();
        } else {
          setOptedOut(true);
          this.remove();
        }
      });
      // Also catch removal via the line item's own "X" remove button, or its
      // quantity stepper being dragged down to 0 — both are separate generic
      // controls (cart-remove-button / quantity-input) and won't otherwise set
      // the opt-out flag, which is why unchecking via quantity used to get
      // silently re-added on the next render (connectedCallback saw !inCart
      // and no opt-out flag, so it called add() again).
      var lineItem = document.querySelector('cart-items [data-variant-id="' + VARIANT_ID + '"]');
      var removeBtn = lineItem && lineItem.querySelector('cart-remove-button a, cart-remove-button .delete-product');
      if (removeBtn) {
        removeBtn.addEventListener('click', function () {
          setOptedOut(true);
        });
      }
      var qtyInput = lineItem && lineItem.querySelector('quantity-input .quantity__input');
      if (qtyInput) {
        qtyInput.addEventListener('change', function () {
          if (parseInt(qtyInput.value, 10) === 0) setOptedOut(true);
        });
      }

      // No real products in the cart → never auto-add. If protection is the only
      // thing left (last product just removed), clean it up (mirrors gift-wrapping.js).
      if (this.otherItems <= 0) {
        if (this.inCart) this.remove();
        return;
      }

      // Enabled by default (opt-out). Reconcile the checkbox + cart on every render.
      if (optedOut()) {
        this.check.checked = false;
        if (this.inCart) this.remove();
      } else {
        this.check.checked = true;
        if (!this.inCart && !addInFlight) this.add();
      }
    }

    // Section ids the <mini-cart> knows how to re-render (keys must match renderContents).
    sections() {
      if (this.miniCart && typeof this.miniCart.getSectionsToRender === 'function') {
        return this.miniCart.getSectionsToRender().map((s) => s.id);
      }
      return [];
    }

    setLoading(on) {
      this.classList.toggle('is-loading', on);
      if (this.check) this.check.disabled = on;
    }

    render(response) {
      this.miniCart && this.miniCart.renderContents(response);
    }

    add() {
      if (this.inCart || addInFlight) return; // already in cart, or another instance's add is already in flight
      addInFlight = true;
      this.setLoading(true);

      var body = JSON.stringify({
        items: [{ id: VARIANT_ID, quantity: 1 }],
        sections: this.sections(),
        sections_url: window.location.pathname
      });
      var url = (window.theme && theme.routes && theme.routes.cart_add_url) ? theme.routes.cart_add_url : '/cart/add.js';

      fetch(url, { ...fetchConfig(), ...{ body } })
        .then((r) => r.json())
        .then((response) => {
          this.setLoading(false);
          addInFlight = false;
          if (response.status) { // add error (e.g. sold out / unavailable)
            if (this.check) this.check.checked = false;
            return;
          }
          this.inCart = true;
          this.render(response);
        })
        .catch(() => {
          this.setLoading(false);
          addInFlight = false;
          if (this.check) this.check.checked = false;
        });
    }

    remove() {
      this.setLoading(true);

      var body = JSON.stringify({
        id: String(VARIANT_ID),
        quantity: 0,
        sections: this.sections(),
        sections_url: window.location.pathname
      });
      var url = (window.theme && theme.routes && theme.routes.cart_change_url) ? theme.routes.cart_change_url : '/cart/change.js';

      fetch(url, { ...fetchConfig(), ...{ body } })
        .then((r) => r.json())
        .then((response) => {
          this.setLoading(false);
          this.inCart = false;
          this.render(response);
        })
        .catch(() => {
          this.setLoading(false);
          if (this.check) this.check.checked = true;
        });
    }
  }

  if (!customElements.get('shipping-protection')) {
    customElements.define('shipping-protection', ShippingProtection);
  }
})();
